import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { Domain, Identity } from 'models';
import { usePermissions } from 'models/user/permissions';

import {
    Card,
    DropdownItem,
    DropdownSort,
    Filter,
    FilterSetController,
    ToastContext,
    WeezHeader,
    Status,
    Table,
    TableCell,
    TableBody,
    TableRow,
    TableCellDropdown,
    Dropdown,
    Button,
    Icon
} from '@weezevent/nacre';

import { InfoTextTooltip, LoadingCard, Paginator, SauronEmptyCard } from 'components';
import { DomainCreationModal, AddOrganizationModal } from './components';
import { useFilters, usePageSize, useCurrentPage, useSearch, handleSearchQuery } from 'hooks/useFilters';

import css from '../emails.module.css';

const spec = {
    multiple: ['verification_status__in'],
    fields: ['verification_status__in', 'search', 'ordering', 'organization'],
    date: [],
    form: [],
    aliases: {}
};

export const Domains = ({ organizationId }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const permissions = usePermissions();

    const [addDomainModal, setAddDomainModal] = React.useState(false);
    const [addOrganizationModal, setAddOrganizationModal] = React.useState(null);

    const [filters, setFilters] = useFilters(spec, organizationId ? { organization: organizationId } : {});
    const [perPageCount, setPerPageCount] = usePageSize(50);
    const [currentPage, setCurrentPage] = useCurrentPage(0);
    const [search, setSearch] = useSearch();
    const [ordering, setOrdering] = React.useState('name');

    const StatusOptions = [
        { key: 'pending', text: t('sauron.emails.domains.status.pending'), name: 'pending', color: 'orange' },
        { key: 'success', text: t('sauron.emails.domains.status.success'), name: 'success', color: 'green' },
        { key: 'failed', text: t('sauron.emails.domains.status.failed'), name: 'failed', color: 'red' }
    ];
    const sortByNames = [
        { key: 'name', label: t('sauron.emails.domains.sort.a-z') },
        { key: '-name', label: t('sauron.emails.domains.sort.z-a') }
    ];

    const [domains, loadingDomains, syncDomains] = Domain.useApiModel(
        {
            organizationId,
            cache: false,
            allow_cache: false,
            query: {
                limit: perPageCount,
                offset: currentPage * perPageCount,
                ordering,
                search,
                ...handleSearchQuery(spec, filters, true)
            },
            onError: () => {
                toast.error(t('sauron.toasts.error'));
            }
        },
        [filters, perPageCount, currentPage, search, ordering, organizationId]
    );

    const [identities] = Identity.useApiModel(
        {
            organizationId,
            cache: false,
            allow_cache: false,
            onError: () => {
                toast.error(t('sauron.toasts.error'));
            },
            launch: Boolean(organizationId)
        },
        [organizationId]
    );

    const enrichedDomains = React.useMemo(() => {
        if (organizationId)
            return domains?.map(d => {
                d.isUsed(identities);
                return d;
            });
        return domains;
    }, [organizationId, domains, identities]);

    const domainsCount = React.useMemo(() => {
        if (loadingDomains) {
            return '--';
        }
        return Number.parseInt(domains?.__meta?.headers.get('total-count')) || 0;
    }, [domains, loadingDomains]);

    return (
        <>
            <WeezHeader
                rightComponent={[
                    permissions.isAdmin() && !organizationId ? (
                        <Button primary onClick={() => setAddDomainModal(true)} icon={<Icon name="plus" />} label={t('sauron.emails.domains.add-domain')} />
                    ) : null
                ]}
                title={t('sauron.emails.domains.title')}
            >
                {t('sauron.emails.domains.subtitle')}
            </WeezHeader>

            <Card>
                <div className={css['card-header']}>
                    <FilterSetController
                        className={css['filter-block']}
                        total={domainsCount}
                        labelTotal={t('common.domain', { count: domainsCount })}
                        labelFilters={t('sauron.emails.domains.filters.filter')}
                        labelReset={t('sauron.emails.domains.filters.label-reset')}
                        initialValues={filters}
                        onChange={setFilters}
                        onReset={() => {
                            setFilters({});
                            setCurrentPage(0);
                            setPerPageCount(50);
                        }}
                        search={{
                            onChange: (_, value) => setSearch(value),
                            handleReset: () => setSearch(''),
                            placeholder: t('sauron.emails.domains.filters.search'),
                            value: search
                        }}
                    >
                        <Filter
                            index="1"
                            multiple
                            labelSelectAll={t('sauron.emails.domains.filters.select-all')}
                            labelName={t('sauron.emails.domains.status.label')}
                            labelCTA={t('sauron.emails.domains.filters.label-cta')}
                            name="verification_status__in"
                            options={StatusOptions}
                        />
                    </FilterSetController>
                </div>
                <LoadingCard loading={loadingDomains}>
                    {domainsCount > 0 ? (
                        <Table>
                            <Table.THead>
                                <Table.Tr isHeader>
                                    <TableCell className={css['domain-row']}>
                                        <DropdownSort
                                            active={['name', '-name'].includes(ordering)}
                                            label={t(`common.name`)}
                                            name="sort"
                                            ordering={ordering}
                                            items={sortByNames.map(item => (
                                                <DropdownItem.Icons key={item.key} item={item.label} checked={ordering === item.key} onClick={() => setOrdering(item.key)} />
                                            ))}
                                        />
                                    </TableCell>
                                    {!organizationId && <TableCell className={css['availability-row']}>{t(`sauron.emails.domains.availability`)}</TableCell>}
                                    <TableCell className={css['status-row']}>{t(`sauron.emails.domains.status.label`)}</TableCell>
                                    <TableCell className={css['actions-row']}>{t(`sauron.emails.domains.actions.label`)}</TableCell>
                                </Table.Tr>
                            </Table.THead>

                            {enrichedDomains?.map((domain, index) => (
                                <TableBody key={index}>
                                    <TableRow className={css['domains-table']} onClick={() => history.push(`/emails/domains/${domain.name}/`)}>
                                        <TableCell className={css['domain-row']}>{domain.name}</TableCell>
                                        {!organizationId && (
                                            <TableCell className={css['availability-row']}>
                                                {domain.organizations.length === 0
                                                    ? '-'
                                                    : `${domain.organizations.length} ${t('sauron.organizations.organization', { count: domain.organizations.length })}`}
                                            </TableCell>
                                        )}
                                        <TableCell className={css['status-row']}>
                                            <Status
                                                color={StatusOptions.find(status => status.key === domain.verification_status).color}
                                                text={t(`sauron.emails.domains.status.${domain.verification_status}`)}
                                            />
                                        </TableCell>
                                        <TableCellDropdown>
                                            {!organizationId ? (
                                                <>
                                                    <Dropdown.Item
                                                        key={0}
                                                        item={t('sauron.emails.domains.actions.see-informations')}
                                                        onClick={() => history.push(`/emails/domains/${domain.name}/`)}
                                                    />
                                                    <Dropdown.Item
                                                        key={1}
                                                        item={t('sauron.emails.domains.actions.add-organisation')}
                                                        onClick={() => {
                                                            setAddOrganizationModal({ domain });
                                                        }}
                                                    />
                                                </>
                                            ) : (
                                                <InfoTextTooltip
                                                    className={css['action-delete-tooltip']}
                                                    title={t('sauron.emails.domains.actions.remove-domain.tool-tip')}
                                                    tooltip={domain.is_used}
                                                >
                                                    <Dropdown.Item
                                                        key={0}
                                                        disabled={domain.is_used}
                                                        deleteItem
                                                        item={t('sauron.emails.domains.actions.remove-domain.label')}
                                                        onClick={() => {
                                                            domain.id = domain.name;
                                                            domain.delete({ organizationId }).then(() => syncDomains());
                                                        }}
                                                    />
                                                </InfoTextTooltip>
                                            )}
                                        </TableCellDropdown>
                                    </TableRow>
                                </TableBody>
                            ))}
                        </Table>
                    ) : (
                        <SauronEmptyCard subTitle={t('sauron.emails.domains.no-domains')} />
                    )}
                </LoadingCard>
            </Card>
            {!loadingDomains && <Paginator currentPage={currentPage} onPageChange={setCurrentPage} pageSize={perPageCount} totalCount={domainsCount} />}
            {addDomainModal && <DomainCreationModal open={addDomainModal} onClose={() => setAddDomainModal(false)} />}
            {addOrganizationModal && (
                <AddOrganizationModal open={addOrganizationModal} onClose={() => setAddOrganizationModal(null)} domain={addOrganizationModal.domain} syncDomain={syncDomains} />
            )}
        </>
    );
};
